import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';
import { ApiLocalPortLabel } from '../../local/dto/port-label.dto';
import { SimpleQrDto } from '../../elite/types/qr-label-zpl';
import { PrintLabelPalletDto } from '../../local/dto/label-pallet.dto';
import { ApiLocalPlasticLabel } from '../../local/dto/plastic-label.dto';
import { PartScanApiRoutesDto } from '../../elite/dto/part-scan.dto';
import { PackingBoxDto } from '../../elite/dto/packing-box.dto';

@Injectable({ providedIn: 'root' })
export class ApiFilesBarCodeService {
  http = inject(HttpClient);
  urlRoute = `${environment.otherApis.files}/api/code`;
  constructor() {}
  getLabelZpl(code: string, time: string, date: string): Observable<string> {
    const params = new HttpParams()
      .set('code', code)
      .set('time', time)
      .set('date', date);

    return this.http.get(this.urlRoute + '/get-codes/genbarcode/zebra', {
      params,
      responseType: 'text',
    });
  }

  getPalletLabelZpl(dto: PrintLabelPalletDto): Observable<{
    data: string;
  }> {
    return this.http.post<{
      data: string;
    }>(this.urlRoute + '/get-codes/pallet/zebra', dto);
  }

  getPackingBoxLabel(
    dto: ApiLocalPortLabel['printLabel']['body']
  ): Observable<string[]> {
    return this.http.post<string[]>(
      this.urlRoute + '/get-codes/pakingbox/zebra',
      dto
    );
  }
  getPackingBoxLabelWip(
    dto: ApiLocalPortLabel['printLabel']['body']
  ): Observable<string[]> {
    return this.http.post<string[]>(
      this.urlRoute + '/get-codes/pakingboxwip/zebra',
      dto
    );
  }
  identificationWorkOrderLabel(
    data: ApiLocalPortLabel['printLabel']['body']
  ): Observable<string[]> {
    if (data.type === 'project') {
      return this.getProjectLabelZpl(data);
    }
    if (data.type === 'packing-box-wip') {
      return this.getPackingBoxLabelWip(data);
    } else {
      return this.getPackingBoxLabel(data);
    }
  }
  getQrLabelZpl(dto: SimpleQrDto[]): Observable<string[]> {
    return this.http.post<string[]>(
      this.urlRoute + '/get-codes/gensimpleqr/zebra',
      { data: dto }
    );
  }

  getProjectLabelZpl(
    data: ApiLocalPortLabel['printLabel']['body']
  ): Observable<string[]> {
    return this.http.post<string[]>(
      this.urlRoute + '/get-codes/project/zebra',
      { ...data }
    );
  }

  printPlasticInspectionLabel(
    data: ApiLocalPlasticLabel['printInspectionLabel']
  ): Observable<string[]> {
    return this.http.post<string[]>(
      this.urlRoute + '/get-codes/plastic/inspection/zebra',
      { ...data }
    );
  }

  printPlasticPartLabel(
    data: ApiLocalPlasticLabel['printPartLabel']
  ): Observable<string[]> {
    return this.http.post<string[]>(
      this.urlRoute + '/get-codes/plastic/part/zebra',
      { ...data }
    );
  }

  printFinishGoodQr(id: number, partNumber: string): Observable<string> {
    return this.http.get(this.urlRoute + '/get-codes/finished-good/qr-code', {
      responseType: 'text',
      params: new HttpParams()
        .set('id', id.toString())
        .set('partNumber', partNumber),
    });
  }

  // -----------------------------------------------------------------------
  // Private (customer-specific) labels
  //
  // Bodies are mirrored from the backend DTOs
  // (libs/api-files/codes/src/dto/*-label.dto.ts) and declared inside
  // `PackingBoxDto` so all packing-box-related shapes stay in one place.
  // Each endpoint returns a single ZPL string ready to be sent to the Zebra
  // printer.
  //
  // The payload is sent as a JSON body (POST). `undefined` properties are
  // dropped by `JSON.stringify` so the backend only sees the fields the
  // caller actually supplied; `null` / `''` values are forwarded as-is, on
  // the assumption that the backend DTOs treat them with `@IsOptional()`.
  // -----------------------------------------------------------------------

  privateLabelSkyJackZplCode(
    body: PackingBoxDto['skyjackLabel']['body']
  ): Observable<{ zpl: string }> {
    return this.http.post<{ zpl: string }>(
      `${this.urlRoute}/private-label/skyjack`,
      body
    );
  }

  privateLabelGabrielZplCode(
    body: PackingBoxDto['gabrielLabel']['body']
  ): Observable<{ zpl: string }> {
    return this.http.post<{ zpl: string }>(
      `${this.urlRoute}/private-label/gabriel`,
      body
    );
  }

  privateLabelSonusZplCode(
    body: PackingBoxDto['sonusLabel']['body']
  ): Observable<{ zpl: string }> {
    return this.http.post<{ zpl: string }>(
      `${this.urlRoute}/private-label/sonus`,
      body
    );
  }

  privateLabelHendricksonZplCode(
    body: PackingBoxDto['hendricksonLabel']['body']
  ): Observable<{ zpl: string }> {
    return this.http.post<{ zpl: string }>(
      `${this.urlRoute}/private-label/hendrickson`,
      body
    );
  }

  printCompoundingLabel(data: {
    partName: string;
    workOrder: string;
    quantity: number;
    ids: number[];
    lotNumber?: string;
  }): Observable<string[]> {
    return this.http.post<string[]>(
      this.urlRoute + '/get-codes/compounding/zebra',
      {
        ...data,
      }
    );
  }

  generateQrFile(
    dto: PartScanApiRoutesDto['generateQr']['body']
  ): Observable<Blob> {
    return this.http.get(this.urlRoute + '/get-codes/genqr/image', {
      params: { ...dto },
      responseType: 'blob',
    });
  }
}
